import React, { useEffect } from 'react';
import styles from './ContextMenu.module.scss';
import { Control, Image, Language } from '@yleisradio/areena-types';
import ThumbsUpIcon from '../common/assets/thumbs-up.svg';
import ThumbsDownIcon from '../common/assets/thumbs-down.svg';
import PlayIcon from '../common/assets/play.svg';
import CancelIcon from '../common/assets/close.svg';
import Checked from '../common/assets/checked.svg';
import { useControl } from '../hooks/useControl';
import { useNotificationContext } from '../contexts/NotificationContext/NotificationContext';
import { getTranslation } from '../localizations/translations';

type ContextMenuButtonProps = {
  control: Control;
  isPressed: boolean;
  languageCode: Language;
  clearPressed: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const getIcon = (icon: Image | undefined) => {
  if (!icon) return undefined;
  switch (icon.id) {
    case 'play':
      return PlayIcon;
    case 'cancel':
      return CancelIcon;
    case 'icon-check':
      return Checked;
    case 'thumbs-up':
      return ThumbsUpIcon;
    case 'thumbs-down':
      return ThumbsDownIcon;
    default:
      return undefined;
  }
};

const ContextMenuButton: React.FC<ContextMenuButtonProps> = ({
  children,
  control,
  isPressed,
  languageCode,
  clearPressed,
  ...buttonProps
}) => {
  const Icon = getIcon(control.icon);
  const { action } = useControl(control);
  const { pushNotification, enableNotifications } = useNotificationContext();

  const notifySuccess = (message: string) => {
    enableNotifications();

    pushNotification({
      id: 'contextmenu_action_successful',
      message,
      messageType: 'success',
    });
  };

  const notifyFailure = () => {
    enableNotifications();

    pushNotification({
      id: 'contextmenu_action_failed',
      message: getTranslation('generic_error', languageCode),
      messageType: 'error',
    });
  };

  const executeAction = () => {
    if (action) {
      action()
        .then(() => {
          if (control.type === 'operator') {
            if (control.tag === 'rating_down' || control.tag === 'rating_up') {
              notifySuccess(
                getTranslation('toast_thank_you_feedback', languageCode)
              );
            } else if (control.tag === 'mark_finished') {
              notifySuccess(
                getTranslation('toast_continue_list_removed', languageCode)
              );
            }
          }
        })
        .catch(() => {
          notifyFailure();
        });
    }
  };

  useEffect(() => {
    if (isPressed) {
      executeAction();
      clearPressed();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPressed]);

  return (
    <button
      className={styles.contextMenu__button}
      tabIndex={1}
      {...buttonProps}
    >
      {Icon && (
        <span className={styles.icon}>
          <Icon />
        </span>
      )}
      {control.title}
    </button>
  );
};

export default ContextMenuButton;
